import { MenuOptionType, useContextMenuMESF } from "@dexteel/mesf-core";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { GridButtons } from "@/controls/buttons/GridButtons";
import {
	BreakRule,
	BreakRulePreset,
	ShiftHours,
} from "@/shared/models/BreakRule";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { GroupCellRenderer } from "../components/GroupCellRenderer";
import type { BreakGridRow } from "../utils/gridRows";
import { formatMinutes } from "../utils/minutes";

export type BreakRuleModal = "create" | "update" | "delete" | "";

export type GroupTarget = {
	kind: "group";
	level: number;
	AssetId: number;
	AssetName: string;
	ShiftHours?: ShiftHours;
	DefaultShiftHours: ShiftHours;
};
export type MenuTarget = BreakGridRow | GroupTarget | undefined;

type Props = {
	setSelectedRowData: (rule: BreakRule) => void;
	setShowModal: (modal: BreakRuleModal) => void;
	setPreset: (preset: BreakRulePreset | null) => void;
	showContextMenu: ReturnType<typeof useContextMenuMESF>["showContextMenu"];
};

export const autoGroupColumnDef: ColDef<BreakGridRow> = {
	headerName: "Area / Line / Shift",
	flex: 2,
	minWidth: 220,
	headerClass: "left-aligned-header",
	cellStyle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
	},
	cellRendererParams: { suppressCount: true, innerRenderer: GroupCellRenderer },
};

export const useTableData = ({
	setSelectedRowData,
	setShowModal,
	setPreset,
	showContextMenu,
}: Props) => {
	const columnDefs: ColDef<BreakGridRow>[] = [
		// Hidden, kept for the quick filter (tree groups are filler nodes)
		{ field: "ParentAssetName", headerName: "Area", hide: true },
		{ field: "AssetName", headerName: "Line", hide: true },
		{
			field: "ShiftHours",
			headerName: "Shift",
			hide: true,
			getQuickFilterText: (params) => `${params.value}h shifts`,
		},
		{ field: "Label", headerName: "Break", flex: 2, minWidth: 120 },
		{
			field: "OffsetMinutes",
			headerName: "Starts (into shift)",
			flex: 1,
			minWidth: 130,
			valueFormatter: (params) => formatMinutes(params.value),
		},
		{
			field: "DurationMinutes",
			headerName: "Duration",
			flex: 1,
			minWidth: 100,
			valueFormatter: (params) => formatMinutes(params.value),
		},
		{
			field: "IsSpell",
			headerName: "Spell",
			flex: 1,
			minWidth: 80,
			cellDataType: "text",
			cellRenderer: (params: ICellRendererParams) =>
				params.value ? <CheckIcon fontSize="small" color="action" /> : null,
			cellStyle: { display: "flex", alignItems: "center" },
		},
		{
			colId: "actions",
			cellRendererSelector: (params: ICellRendererParams<BreakGridRow>) =>
				params.node.group || params.data?.kind !== "rule"
					? undefined
					: {
							component: GridButtons,
							params: {
								setId: setSelectedRowData,
								id: params.data,
								edit: true,
								setShowModal,
								showContextMenu: () =>
									showContextMenu(
										event as unknown as React.MouseEvent,
										params.data,
										"breakRulesContext",
									),
							},
						},
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: false,
			headerName: "",
			sortable: false,
			flex: 1,
			maxWidth: 220,
			filter: false,
			autoHeaderHeight: true,
		},
	];

	const newBreak = (preset: BreakRulePreset | null): MenuOptionType => ({
		name: preset ? "New Break here" : "New Break",
		key: "new_break_rule",
		onClick: () => {
			setPreset(preset);
			setShowModal("create");
		},
		icon: <PlaylistAddIcon />,
	});

	const getMenuOptions = (target: MenuTarget): MenuOptionType[] => {
		if (!target) return [newBreak(null)];

		if (target.kind === "add" || (target.kind === "group" && target.ShiftHours))
			return [
				newBreak({
					AssetId: target.AssetId,
					ShiftHours: target.ShiftHours as ShiftHours,
				}),
			];

		if (target.kind === "group") return [newBreak(null)];

		return [
			newBreak(null),
			{
				name: "Edit Break",
				key: "edit_break_rule",
				onClick: () => {
					setSelectedRowData(target);
					setShowModal("update");
				},
				icon: <EditIcon />,
			},
			{
				name: "Delete Break",
				key: "delete_break_rule",
				onClick: () => {
					setSelectedRowData(target);
					setShowModal("delete");
				},
				icon: <DeleteIcon />,
			},
		];
	};

	return { columnDefs, getMenuOptions };
};
